/**
 * License to Permissions Mapping
 *
 * Defines which permissions are granted by each license type.
 * Used by CaslAbilityFactory to build user abilities based on licenses.
 *
 * @see Issue #293 - Permission System Enhancement
 * @see CaslAbilityFactory in 2-api-gateway/src/auth/casl/casl-ability.factory.ts
 */

import { BusinessLicenseType, Permission, type PermissionValue, UserLicenseType } from '../enums';

/**
 * Permissions granted by user license types.
 *
 * Note: State-specific licenses (STATE_LOAN_OFFICER) grant permissions
 * with geographic conditions. The actual permission is combined with
 * the license's issuingAuthority (state code) in CaslAbilityFactory.
 */
export const USER_LICENSE_PERMISSIONS: Record<UserLicenseType, PermissionValue[]> = {
  /**
   * NMLS Individual License (MLO)
   * - Nationwide capability for mortgage loan origination
   * - Access to consumer PII for compliance
   * - Can pull credit reports
   */
  [UserLicenseType.NMLS_INDIVIDUAL]: [
    Permission.loan.originate,
    Permission.consumer.piiAccess,
    Permission.consumer.creditPull,
    Permission.consumer.financialsView,
    Permission.documents.sensitiveRead,
  ],

  /**
   * State Loan Officer License
   * - State-specific loan origination capability
   * - Processing permissions within licensed state
   * - Note: Applied with propertyState condition in CaslAbilityFactory
   */
  [UserLicenseType.STATE_LOAN_OFFICER]: [Permission.loan.originate, Permission.loan.process],

  /**
   * State Broker License
   * - State-specific broker activities
   * - Can facilitate loans but not fund them
   */
  [UserLicenseType.STATE_BROKER]: [Permission.loan.originate, Permission.loan.process],
};

/**
 * Permissions granted by business license types.
 *
 * These permissions are available to all users in a business that holds
 * the license, subject to their individual role permissions.
 * State-specific licenses apply geographic conditions.
 */
export const BUSINESS_LICENSE_PERMISSIONS: Record<BusinessLicenseType, PermissionValue[]> = {
  /**
   * NMLS Company License
   * - Business-level mortgage company registration
   * - Enables company to operate as MLO sponsor
   */
  [BusinessLicenseType.NMLS_COMPANY]: [Permission.consumer.piiAccess, Permission.consumer.creditPull],

  /**
   * State Lender License
   * - Can fund loans in the licensed state
   * - Required for direct lending operations
   */
  [BusinessLicenseType.STATE_LENDER]: [Permission.loan.fund, Permission.loan.underwrite],

  /**
   * State Broker License
   * - Business can broker loans in the state
   * - Cannot fund directly
   */
  [BusinessLicenseType.STATE_BROKER]: [Permission.loan.process],

  /**
   * State Servicer License
   * - Can service loans in the licensed state
   * - Post-funding loan management
   */
  [BusinessLicenseType.STATE_SERVICER]: [Permission.loan.service],
};

/**
 * License types that grant state-specific (geographic) permissions.
 * When evaluating these licenses, the issuingAuthority should be used
 * as a condition (propertyState === issuingAuthority).
 */
export const STATE_SPECIFIC_USER_LICENSES: readonly UserLicenseType[] = [
  UserLicenseType.STATE_LOAN_OFFICER,
  UserLicenseType.STATE_BROKER,
] as const;

export const STATE_SPECIFIC_BUSINESS_LICENSES: readonly BusinessLicenseType[] = [
  BusinessLicenseType.STATE_LENDER,
  BusinessLicenseType.STATE_BROKER,
  BusinessLicenseType.STATE_SERVICER,
] as const;

/**
 * Check if a user license type is state-specific.
 */
export function isStateSpecificUserLicense(type: UserLicenseType): boolean {
  return STATE_SPECIFIC_USER_LICENSES.includes(type);
}

/**
 * Check if a business license type is state-specific.
 */
export function isStateSpecificBusinessLicense(type: BusinessLicenseType): boolean {
  return STATE_SPECIFIC_BUSINESS_LICENSES.includes(type);
}

/**
 * Get permissions granted by a user license type.
 */
export function getPermissionsForUserLicense(type: UserLicenseType): PermissionValue[] {
  return USER_LICENSE_PERMISSIONS[type] || [];
}

/**
 * Get permissions granted by a business license type.
 */
export function getPermissionsForBusinessLicense(type: BusinessLicenseType): PermissionValue[] {
  return BUSINESS_LICENSE_PERMISSIONS[type] || [];
}

/**
 * US States for license geographic validation.
 */
export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'PR',
  'VI',
  'GU',
  'AS',
  'MP',
] as const;

export type USState = (typeof US_STATES)[number];

/**
 * Validate if a string is a valid US state code.
 */
export function isValidUSState(state: string): state is USState {
  return US_STATES.includes(state as USState);
}
